import styled, { css } from 'styled-components';
import { Autocomplete, Grid, InputBase, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import Signature from '@uiw/react-signature';
import * as SmartySDK from 'smartystreets-javascript-sdk';
import * as sdkUtils from 'smartystreets-javascript-sdk-utils';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import Button from './components/button';
import MaskedInput from 'react-text-mask/dist/reactTextMask';
import { createNumberMask } from 'text-mask-addons';
import { useSearchParams } from 'react-router';
import Api from './utils/api';
import pay from './Pay';

const numberMask = createNumberMask({
    prefix: '$',
    suffix: '',
    allowDecimal: true,
    decimalLimit: 2,
});

const CustomInput = forwardRef((props, ref) => {
    return (
        <StyledTextField
            size={'small'}
            {...props}
            error={props.error}
            inputRef={ref}
        />
    );
});

function svgToPngBase64(svgString, width, height) {
    return new Promise((resolve, reject) => {
        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // Create image
        const img = new Image();
        img.onload = () => {
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png').split(',')[1]);
        };
        img.onerror = reject;

        // Load SVG into image
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        img.src = URL.createObjectURL(blob);
    });
}

const App = () => {
    const [suggestions, setSuggestions] = useState({ result: [] });
    const [sigError, setSigError] = useState(false);
    const [formStatus, setFormStatus] = useState('');
    const [salesReps, setSalesReps] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [loading, setLoading] = useState(false);
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm();
    const $sig = useRef(null);
    const handle = (evn) => $sig.current?.clear();
    const [searchParams] = useSearchParams();
    const btnColor = searchParams.get('btnColor') || 'black';
    const btnTxtColor = searchParams.get('btnTxtColor') || 'white';
    const txtColor = searchParams.get('txtColor') || 'black';
    const vendor = searchParams.get('vendor') || '';
    const salesRepName = watch('sales_rep_name');

    const SmartyCore = SmartySDK.core;
    const websiteKey = process.env.REACT_APP_SMARTY;
    const smartySharedCredentials = new SmartyCore.SharedCredentials(
        websiteKey,
    );

    const autoCompleteClient = new SmartyCore.ClientBuilder(smartySharedCredentials).withLicenses(['us-autocomplete-pro-cloud']).buildUsAutocompleteProClient();

    const createUSLookup = (query, hasSecondaries) => {
        const lookup = new SmartySDK.usAutocompletePro.Lookup(query);
        if (hasSecondaries) {
            lookup.selected = query;
        }
        return lookup;
    };

    const queryAutocompleteForSuggestions = async (query, addressId, hasSecondaries = false) => {
        if (query !== '') {
            const client = autoCompleteClient;
            const lookup = createUSLookup(query, hasSecondaries);
            lookup.country = 'US';
            try {
                const results = await client.send(lookup);
                setSuggestions(results);
            } catch (error) {
                console.warn(error);
            }
        } else {
            setSuggestions({ result: [] });
        }
    };

    const optionLabel = (op) => {
        let returnStr = '';
        if (op.streetLine) {
            returnStr += op.streetLine;
        }
        if (op.city) {
            returnStr += ', ' + op.city;
        }
        if (op.state) {
            returnStr += ', ' + op.state;
        }
        if (op.zipCode) {
            returnStr += ',' + op.zipCode;
        }

        return returnStr;
    };

    const getData = async () => {
        // Only fetch sales reps and equipment if a vendor is provided
        if (vendor) {
            const reps = await Api.get('/salesreps?vendor=' + vendor);
            const eq = await Api.post('/equipment', { vendor: vendor });
            setSalesReps(reps.data.Sources || []);
            setEquipment(eq.data || []);
            
            // If there's only one piece of equipment, set it as default
            if (eq.data && eq.data.length === 1) {
                setValue('equipment_makemodel', eq.data[0].name);
            }
        }
    };

    // Calculate average equipment price to use as fallback
    const getAverageEquipmentPrice = () => {
        if (equipment && equipment.length > 0) {
            const totalPrice = equipment.reduce((sum, item) => sum + (item.default_price || 0), 0);
            return (totalPrice / equipment.length).toFixed(2);
        }
        return "0";
    };

    const compareOptions = (option, value) => {
        return option.streetLine === value.streetLine && option.city === value.city && option.state === value.state && option.zipCode === value.zipCode;
    };

    useEffect(() => {
        getData();
    }, [vendor]);

    const onSubmit = useCallback(async (data) => {
        setLoading(true);
        setFormStatus('');
        const svgElement = $sig.current?.svg;
        const hasPaths = svgElement.querySelectorAll('path[d]').length > 0;
        if (!hasPaths) {
            setLoading(false);
            console.log('Signature is required');
            setSigError(true);
            return;
        } else {
            setSigError(false);
        }
        try {
            const svgelm = $sig.current?.svg?.cloneNode(true);
            const clientWidth = $sig.current?.svg?.clientWidth;
            const clientHeight = $sig.current?.svg?.clientHeight;
            svgelm.removeAttribute('style');
            svgelm.setAttribute('width', `${clientWidth}px`);
            svgelm.setAttribute('height', `${clientHeight}px`);
            svgelm.setAttribute('viewbox', `${clientWidth} ${clientHeight}`);
            const d = new XMLSerializer().serializeToString(svgelm);
            const signature = await svgToPngBase64(d, clientWidth, clientHeight);
            const payload = {
                ...data,
                signature,
                business_address: selectedAddress,
            };

            // If page is embedded in an iframe, include the parent URL in the payload
            if (window.location !== window.parent.location) {
                payload.parent_url = document.referrer;
            } else {
                payload.parent_url = window.location.href;
            }

            if (payload.amount === '' || payload.amount === undefined) {
                payload.amount = getAverageEquipmentPrice();
            }

            if (vendor) {
                payload.vendor = vendor;
            }

            await Api.post('/application', payload);
            setFormStatus('success');

        } catch (e) {
            setFormStatus('error');
            setLoading(false);
        }

    }, [selectedAddress, vendor, equipment]);

    const selectAddress = (address) => {
        setSelectedAddress(address);
        setValue('business_city', address.city);
        setValue('business_state', address.state);
    };

    if (formStatus === 'success') {
        return (
            <PageContainer txtColor={txtColor}>
                <div style={{ textAlign: 'center' }}>
                    <h1>Thank you for your submission!</h1>
                    <p>We will be in touch with you shortly.</p>
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer txtColor={txtColor}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label req>First Name</Label>
                            <Input
                                disabled={loading}
                                {...register('first', { required: true })}
                                error={errors.first}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label req>Last Name</Label>
                            <Input
                                disabled={loading}
                                {...register('last', { required: true })}
                                error={errors.last}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label req>Ownership %</Label>
                            <Input
                                type="number"
                                min={0}
                                max={100}
                                disabled={loading}
                                onInput={(e) => {
                                    if (e.target.value > 100)
                                        e.target.value = 100;
                                }}
                                {...register('ownership', {
                                    required: true,
                                    min: 0,
                                    max: 100,
                                })}
                                error={errors.ownership}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label req>Business Name</Label>
                            <Input
                                disabled={loading}
                                {...register('business_name', {
                                    required: true,
                                })}
                                error={errors.business_name}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label req>Business Address</Label>
                            <Autocomplete
                                disabled={loading}
                                size={'small'}
                                autoComplete
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        selectAddress(newValue);
                                    }
                                }}
                                onInputChange={(event, newInputValue) => {
                                    queryAutocompleteForSuggestions(newInputValue);
                                }}
                                isOptionEqualToValue={(option, value) => compareOptions(option, value)}
                                getOptionLabel={optionLabel}
                                options={suggestions.result}
                                renderInput={(params) => (
                                    <CustomInput {...params}
                                                 error={errors.business_address}
                                                 ref={params.InputProps.ref} {...register('business_address', {
                                        required: true,
                                    })} />
                                )}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label>Business Website</Label>
                            <Input
                                disabled={loading}
                                {...register('website')}
                                error={errors.website}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label req>Personal Email</Label>
                            <Input {...register('email', {
                                required: true, pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                },
                            })}
                                   disabled={loading}
                                   error={errors.email}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label req>Cell Phone</Label>
                            <Input
                                as={CustomMaskedInput}
                                mask={[
                                    '(',
                                    /[1-9]/,
                                    /\d/,
                                    /\d/,
                                    ')',
                                    ' ',
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    '-',
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                ]}
                                {...register('phone', {
                                    required: true,
                                    validate: (value) => value.replace(/\D/g, '').length === 10,
                                })}
                                disabled={loading}
                                error={errors.phone}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldContainer>
                            <Label req>SSN</Label>
                            <Input
                                as={CustomMaskedInput}
                                mask={[
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    '-',
                                    /\d/,
                                    /\d/,
                                    '-',
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                ]}
                                {...register('ssn', {
                                    required: true,
                                })}
                                disabled={loading}
                                error={errors.ssn}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={salesRepName === 'other' ? 6 : 12}>
                        <FieldContainer>
                            <Label req={!!vendor}>Sales Representative Name</Label>
                            {vendor && salesReps.length > 0 ? (
                                <Select
                                    {...register('sales_rep_name', {
                                        required: !!vendor,
                                    })}
                                    disabled={loading}
                                    error={errors.sales_rep_name}
                                >
                                    <option value=""></option>
                                    {salesReps.map((rep) => (
                                        <option key={rep.Name} value={rep.Name}>
                                            {rep.Name}
                                        </option>
                                    ))}
                                    <option value="other">Other</option>
                                </Select>
                            ) : (
                                <Input
                                    {...register('sales_rep_name', {
                                        required: !!vendor,
                                    })}
                                    disabled={loading}
                                    error={errors.sales_rep_name}
                                />
                            )}
                        </FieldContainer>
                    </Grid>
                    {salesRepName === 'other' && (
                        <Grid item xs={12} sm={6}>
                            <FieldContainer>
                                <Label>Sales Representative Email</Label>
                                <Input
                                    {...register('sales_email')}
                                    disabled={loading}
                                />
                            </FieldContainer>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <FieldContainer>
                            <Label req={!!vendor}>Equipment Make/Model</Label>
                            {vendor && equipment.length > 0 ? (
                                <Select
                                    {...register('equipment_makemodel', {
                                        required: !!vendor,
                                    })}
                                    error={errors.equipment_makemodel}
                                    disabled={loading}
                                >
                                    <option value=""></option>
                                    {equipment.map((eq) => (
                                        <option key={eq.name} value={eq.name}>
                                            {eq.name}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Input
                                    {...register('equipment_makemodel', {
                                        required: !!vendor,
                                    })}
                                    error={errors.equipment_makemodel}
                                    disabled={loading}
                                />
                            )}
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FieldContainer>
                            <Label>Equipment Price</Label>
                            <Input
                                as={CustomMaskedInput}
                                mask={numberMask}
                                {...register('amount', { required: false })}
                                disabled={loading}
                            />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <FieldContainer>
                            <Label>Additional Info</Label>
                            <TextArea {...register('additional')} />
                        </FieldContainer>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <FieldContainer style={{ position: 'relative' }}>
                            <Label req>Signature</Label>
                            <SigBox error={sigError}>
                                <Signature ref={$sig} readonly={loading} />
                            </SigBox>
                            <div
                                onClick={handle}
                                style={{
                                    position: 'absolute',
                                    right: '0',
                                    cursor: 'pointer',
                                }}
                            >
                                Clear
                            </div>
                        </FieldContainer>
                        <div
                            style={{
                                textAlign: 'center',
                                marginTop: '5px',
                                position: 'relative',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '11px',
                                }}
                            >
                                Sign Above
                            </div>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <Disclaimer>
                            <p>
                                By signing above, I/we authorize lessor and/or
                                debtor and their affiliates, successors or its
                                designee (and any assignee or potential assignee
                                thereof) to obtain consumer and/or business
                                credit reports relating to my/our credit history
                                and/or creditworthiness. Such authorization
                                shall extend to obtaining a credit profile in
                                considering this application and subsequently
                                for the purposes of update, renewal or extension
                                of such credit or additional credit for
                                reviewing or collecting the resulting account.
                                By signing below, I/we affirm my/our identity as
                                the respective individual(s) identified in this
                                application and authorize you to contact me/us
                                at any numbers provided above. I/we also certify
                                that all loan proceeds will be used exclusively
                                for business related purposes. If the business
                                is a corporation or partnership, all authorized
                                owners/principals must sign and include their
                                corporate/partnership title. A Photostat or
                                facsimile copy of this authorization shall be
                                valid as the original.
                            </p>
                            <p>
                                CREDIT RELEASE AUTHORIZATION: I hereby certify
                                that the information contained in this credit
                                application is true and accurate and I/we hereby
                                authorize our banks, trade references, and
                                financial institutions the right to release
                                credit information. In states where permissible,
                                I hereby authorize the filing and recording of
                                UCC financing Statements showing the Secured
                                Party's interest in the equipment and grant the
                                Secured Party the right to execute
                                Lessee's/debtors name thereto. A photostat copy
                                of this authorization shall be as valid as the
                                original. This document may be electronically
                                signed, and any electronic signatures appearing
                                on this Agreement or such other documents are
                                the same as handwritten signatures for the
                                purposes of validity, enforceability, and
                                admissibility.
                            </p>
                            <p>
                                The Federal Equal Credit Opportunity Act
                                prohibits creditors from discriminating against
                                credit applicants on the basis of race, color,
                                religion, national origin, sex, marital status,
                                age (provided the applicant has the capacity to
                                enter into a binding contract), because all or
                                part of the applicant's income derives from any
                                public assistance program or because the
                                applicant has in good faith exercised any right
                                under the Consumer Credit Protection Act. The
                                federal agency that administers compliance with
                                this law is the Federal Trade Commission Equal
                                Credit Opportunity, Washington, D.C. 20580. If
                                your application for business credit is denied,
                                you have the right to a written statement of the
                                specific reasons for the denial. To obtain the
                                statement, please contact Lessor set forth above
                                within 60 days from the date you are notified of
                                our decision. We will send you a written
                                statement of reasons for the denial within 30
                                days of receiving your request for the
                                statement.
                            </p>
                        </Disclaimer>
                    </Grid>
                    <Grid item xs={12} style={{ textAlign: 'center' }}>
                        <Button type="submit" variant={'contained-flat'} color={btnColor} txtColor={btnTxtColor}
                                loading={loading}>
                            Submit Application
                        </Button>
                    </Grid>
                    {formStatus === 'error' && (
                        <Grid item xs={12}>
                            <div style={{ color: '#d90000' }}>
                                There was an error submitting the application. Please
                                try again.
                            </div>
                        </Grid>
                    )}
                </Grid>
            </form>
        </PageContainer>
    );
};

const CustomMaskedInput = forwardRef((props, ref) => {
    return (
        <MaskedInput
            {...props}
            ref={(inputRef) => {
                ref(inputRef ? inputRef.inputElement : null);
            }}
        />
    );
});

const Disclaimer = styled.div`
    padding: 10px;
    font-size: 11px;

    ${({ color }) =>
            color &&
            css`
                color: #${color};
            `}
`;

const PageContainer = styled.div`
    padding: 10px;
    ${({ txtColor }) =>
            txtColor &&
            css`
                color: #${txtColor};
            `}
`;

const Input = styled.input`
    padding: 10px;
    height: 35px;
    border-radius: 5px;
    font-size: 12px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;

    ${({ error }) =>
            error &&
            css`
                border: 2px solid #d90000;
                padding: 9px;
            `}
    &:focus {
        outline: none;
        border: 2px solid #262626;
        padding: 9px;
    }

    &:hover {
        border: 1px solid #262626;
    }
`;

const StyledTextField = styled(TextField)`
    & .MuiInputBase-root {
        height: 35px;
        padding: 12px;
        border-radius: 5px;
        border: none;
        font-family: 'Roboto';
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        box-sizing: border-box;
        background-color: white;

        &.Mui-focused > fieldset {
            border: 2px solid #262626
        }

        &:focus {
            outline: none;
            border: 2px solid #262626;
            padding: 9px;
        }

        & fieldset {
            ${({ error }) =>
                    error &&
                    css`
                        border: 2px solid #d90000;
                        padding: 9px;
                    `}
        }
    }
`;

const SigBox = styled.div`
    padding: 5px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    ${({ error }) =>
            error &&
            css`
                border: 2px solid #d90000;
                padding: 9px;
            `}
`;

const TextArea = styled.textarea`
    padding: 10px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;

    ${({ error }) =>
            error &&
            css`
                border: 2px solid #d90000;
                padding: 9px;
            `}
    &:focus {
        outline: none;
        border: 2px solid #262626;
        padding: 9px;
    }

    ${({ color }) =>
            color &&
            css`
                color: #${color};
            `}
`;

const Prefix = styled.div`
    position: absolute;
    left: 10px;
    top: 0px;
    font-size: 20px;
    color: rgba(0, 0, 0, 0.54);
`;

const Label = styled.div`
    display: flex;
    align-items: center;

    ${({ req }) =>
            req &&
            css`
                &::after {
                    content: '*';
                    color: #d90000;
                    margin-left: 5px;
                }
            `}
`;

const Select = styled.select`
    height: 35px;
    padding: 9px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;

    ${({ error }) =>
            error &&
            css`
                border: 2px solid #d90000;
                padding: 9px;
            `}
    &:focus {
        outline: none;
        border: 2px solid #262626;
        padding: 9px;
    }
`;


const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

export default App;
